import { ErrorBoundary } from '@sentry/react';
import { Drawer, Grid, Stacked } from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue } from 'jotai';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutlet } from 'react-router-dom';
import { isMobileFiltersPanelOpenAtom, rightPanelHeaderAtom } from '@/atoms/sidebar';
import { FULL_WIDTH_GRID, HALF_WIDTH_GRID } from '@/consts/grids';
import { SETTINGS_PANEL } from '@/consts/searchKeys';
import env from '@/env';
import { useCloseSettingsPanel } from '@/hooks/useCloseSettingsPanel';
import { useIsNarrowViewport } from '@/hooks/useIsNarrowViewport';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';
import { useQueryParam } from '@/hooks/useQueryParam';
import { useSetPitchDisplaySettings } from '@/hooks/useSetPitchDisplaySettings/useSetPitchDisplaySettings';
import { MobileFilterButton } from '../button/MobileFilterButton';
import { DebugPanel } from '../debug/DebugPanel';
import { GeneralErrorBoundaryFallback } from '../errorBoundary/ErrorBoundaryFallback';

const calculateMainColumns = (isTouch: boolean, hasRightPanel: boolean) => {
  if (isTouch || !hasRightPanel) return FULL_WIDTH_GRID;
  return HALF_WIDTH_GRID;
};

export const LayoutWithFilters = ({
  filters,
  filtersTitle,
  filtersActions,
  children,
  onClickMobileClose,
  settingsPanel,
  insightComponent,
}: {
  filters?: ReactNode;
  filtersTitle?: string;
  filtersActions?: ReactNode;
  children?: ReactNode;
  onClickMobileClose?: () => void;
  settingsPanel?: ReactNode;
  insightComponent?: ReactNode;
}) => {
  const rightPanelContent = useOutlet();
  const [isMobileFiltersPanelOpen, setIsMobileFiltersPanelOpen] = useAtom(isMobileFiltersPanelOpenAtom);
  const isSettingsPanelOpen = useQueryParam(SETTINGS_PANEL);
  const rightPanelHeader = useAtomValue(rightPanelHeaderAtom);
  const closeSettingsPanel = useCloseSettingsPanel();

  const isTouch = useIsTouchDevice();
  const isNarrowViewport = useIsNarrowViewport();

  useSetPitchDisplaySettings();
  const navigate = useNavigate();
  const { t } = useTranslation(['general', 'visualisation']);

  const closeFiltersPanel = () => {
    onClickMobileClose?.();
    setIsMobileFiltersPanelOpen(false);
  };

  const closeRightPanel = () => navigate(-1);

  return (
    <ErrorBoundary fallback={<GeneralErrorBoundaryFallback />}>
      {/* There is pre-existing custom styling within this project. Follow up required to handle styling inside component library */}
      <div className="h-full overflow-hidden isolate [&>div]:h-full">
        <Stacked direction="row" alignItems="stretch">
          {!isTouch && !rightPanelContent && (
            <div className="w-[320px] shrink-0 relative overflow-hidden">
              <Drawer
                actions={filtersActions}
                disablePadding
                isOpen
                placement="left"
                position="absolute"
                title={filtersTitle}
                width={100}
                testId="filters-drawer"
              >
                {filters}
              </Drawer>
              <Drawer
                closeTitle={t('close')}
                isOpen={!!isSettingsPanelOpen}
                onClose={closeSettingsPanel}
                placement="left"
                position="absolute"
                title={t('appearance', { ns: 'visualisation' })}
                width={100}
                testId="settings-drawer"
              >
                {settingsPanel}
              </Drawer>
            </div>
          )}
          {/* There is pre-existing custom styling within this project. Follow up required to handle styling inside component library */}
          <div className="flex-1 h-full overflow-hidden [&>div]:h-full [&>div]:flex-nowrap [&>div>div]:overflow-hidden">
            <Grid>
              <Grid item xs={calculateMainColumns(isTouch, !!rightPanelContent)}>
                {/* There is pre-existing custom styling within this project. Follow up required to handle styling inside component library */}
                <div className="h-full overflow-y-auto overflow-x-hidden md:py-2">{children}</div>
              </Grid>
              {!isTouch && !!rightPanelContent && (
                <Grid item xs={HALF_WIDTH_GRID}>
                  <Drawer
                    closeTitle={t('close')}
                    disablePadding
                    isOpen
                    onClose={rightPanelHeader?.onClose}
                    placement="right"
                    position="relative"
                    title={rightPanelHeader?.title}
                    width={100}
                    testId="right-panel-drawer"
                  >
                    {rightPanelContent}
                  </Drawer>
                </Grid>
              )}
            </Grid>
          </div>
          {!isNarrowViewport && !rightPanelContent && insightComponent && (
            <div className="w-[360px] shrink-0 h-full">
              <Drawer
                disablePadding
                isOpen
                placement="right"
                position="relative"
                width={100}
                title={t('insights', { ns: 'visualisation' })}
              >
                {insightComponent}
              </Drawer>
            </div>
          )}
        </Stacked>
      </div>
      {isTouch && (
        <>
          <MobileFilterButton />
          <Drawer
            actions={filtersActions}
            closeTitle={t('close')}
            disablePadding
            isOpen={isMobileFiltersPanelOpen && !rightPanelContent}
            onClose={closeFiltersPanel}
            onBackdropClick={closeFiltersPanel}
            placement="bottom"
            position="fixed"
            title={filtersTitle}
            testId="filters-drawer"
          >
            {filters}
          </Drawer>
          <Drawer
            closeTitle={t('close')}
            isOpen={!!isSettingsPanelOpen && !rightPanelContent}
            onClose={closeSettingsPanel}
            onBackdropClick={closeSettingsPanel}
            placement="bottom"
            position="fixed"
            title={t('appearance', { ns: 'visualisation' })}
            testId="settings-panel"
          >
            {settingsPanel}
          </Drawer>
          <Drawer
            closeTitle={t('close')}
            disablePadding
            isOpen={!!rightPanelContent}
            onClose={rightPanelHeader?.onClose}
            onBackdropClick={rightPanelHeader?.onClose ?? closeRightPanel}
            placement="bottom"
            position="fixed"
            title={rightPanelHeader?.title}
            testId="right-panel-drawer"
          >
            {rightPanelContent}
          </Drawer>
        </>
      )}
      {
        /* istanbul ignore next */
        ['development', 'staging'].includes(env.VITE_NODE_ENV) && <DebugPanel />
      }
    </ErrorBoundary>
  );
};
